import axios from "axios";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import DebounceInput from "react-debounce-input";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, Col, Label, Row } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, helpers } from "../utils";

const today = new Date();
let thisMonth = today.getMonth() + 1; // months are zero indexed
const thisDay = today.getDate();
const thisYear = today.getFullYear();
if (thisMonth < 10) {
  thisMonth = `0${thisMonth}`;
}

const startDate = `${thisYear}-01-01`;
const endDate = `${thisYear}-${thisMonth}-${thisDay}`;
const { INPUT_TYPES } = constants;

// these are the names to api Calls as well
const reportTypes = {
  SummaryRpt: "SummaryRpt",
  DetailRpt: "DetailRpt",
  DeliveryRatingRpt: "DeliveryRatingRpt",
  SummaryRptXLS: "SummaryRptXLS",
  DetailRptXLS: "DetailRptXLS",
  RatingRptXLS: "RatingRptXLS"
}

const outputType = {
  PDF: "PDF",
  XLS: "XLS"
}

const FieldObjects = [
  {
    name: "Start Date:",
    type: INPUT_TYPES.date,
    label: "startDate",
    value: startDate
  },
  {
    name: "End Date:",
    type: INPUT_TYPES.date,
    label: "endDate",
    value: endDate
  },
  {
    name: "Supplier (Detail Report):",
    type: INPUT_TYPES.select,
    label: "supplier",
    value: null,
    options: []
  }
];

export default function SupplierPerformanceScorecard(props) {
  //state
  const [downloading, setDownloading] = useState(false);
  const [screenFields, setScreenFields] = useState({
    payload: { startDate: startDate, endDate: endDate, supplier: "", supplierLabel: "" },
    screen: _.cloneDeep(FieldObjects),
    reportType: reportTypes.DetailRpt,
    outputType: outputType.PDF
  });

  useEffect(() => {
    document.title = `Supplier Performance Scorecard`
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    api
      .fetchS("Purchasing/GetListOfVendors", {}, source.token)
      .then((response) => {
        if (response?.data?.success) {
          const list = _.map(screenFields.screen, (x) => {
            if (x.type === INPUT_TYPES.select) {
              x.options = response.data.vendorList;
            }
            return x;
          });
          screenFields.screen = list;
          setScreenFields({ ...screenFields });
        } else {
          throw new Error(response)
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          return "axios request cancelled";
        } else {
          api.catchHandler(err);
        }
      });

    return () => {
      source.cancel("axios request cancelled");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callbackFunc = (event, idx, type, key) => {
    if (type === INPUT_TYPES.date) {
      const value = event?.target?.value;
      screenFields.payload[key] = value;
      setScreenFields({ ...screenFields });
    } else {
      screenFields.screen[idx].value = event;
      screenFields.payload.supplier = event.value;
      screenFields.payload.supplierLabel = event.label; 
      setScreenFields({ ...screenFields });
    }
  };

  const onChangeOutput = (type) => {
    screenFields.outputType = type;
    setScreenFields({ ...screenFields })
  }

  const onChangeReport = (type) => {
    screenFields.reportType = type;
    setScreenFields({ ...screenFields })
  }

  const runReport = () => {
    let payload = Object.assign({}, screenFields.payload)
    payload.startDate = new Date(payload.startDate);
    payload.endDate = new Date(payload.endDate);

    if (screenFields.reportType === reportTypes.DetailRpt && !payload.supplier) return toast.error("Supplier is required for detail reports")

    if (screenFields.outputType === outputType.PDF) {
      // validations must be done before this 
      let title = "";
      switch (screenFields.reportType) {
        case reportTypes.DeliveryRatingRpt:
          title = `Supplier_Rating`
          break;
        case reportTypes.DetailRpt:
          title = `Supplier_${payload.supplier}`
          break;
        case reportTypes.SummaryRpt:
          title = `Supplier_Summary`;
          break;
        default:
          break;
      };
      setDownloading(true);
      api.postDownloadBinary(`Report/${screenFields.reportType}`, payload, constants.MIME_TYPES.pdf, `${title}_ScoreCard_${endDate}`, "There was an error downloading this scorecard pdf")
      .then((res) => {
        if(res?.data === false) {
          toast.error(res.message);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setDownloading(false));
    } else if (screenFields.outputType === outputType.XLS) {
      let defaultName = "";
      let reportXLSEndPoint = "";

      if (screenFields.reportType === reportTypes.SummaryRpt) {
        reportXLSEndPoint = reportTypes.SummaryRptXLS
        defaultName = "Summary"
      } else if (screenFields.reportType === reportTypes.DetailRpt) {
        reportXLSEndPoint = reportTypes.DetailRptXLS
        defaultName = payload.supplier
      } else {
        reportXLSEndPoint = reportTypes.RatingRptXLS
        defaultName = "Rating"
      }
      setDownloading(true);

      api.postGetBlob(`Report/${reportXLSEndPoint}`, payload)
        .then(r => {
          if (!r.data) {
            toast.error(r.message || "The scorecard could not be exported.");
            return;
          }
          let XLSX_FILENAME = `Supplier_${defaultName}_ScoreCard${endDate}_.xlsx`;
          helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX)
        }).catch(api.catchHandler)
        .finally(() => setDownloading(false))
    } else {
      toast.error("Output type not recognized")
    }
  }

  return (
    <DesktopContainer screenName={"Supplier Performance Scorecard"}>
      <div className="filter-header stick-top p-5 mb-3">
          {/* <ServiceThreadSpinner on={downloading}> */}
            {_.map(screenFields.screen, (x, idx) => {
              return <RenderInput key={`${x.name}-axt`} obj={x} callback={callbackFunc} idx={idx} />;
            })}
            <Row className="mt-3">
              <Col xs="3" style={{ textAlign: "left", marginRight: "5px" }}>
                <Label className="text-white" >Report Type:</Label>
              </Col>
              <Col>
                <Row>
                  <Col>
                    <input
                      type="radio"
                      id="detail"
                      name="reportType"
                      defaultChecked={true}
                      onChange={() => onChangeReport(reportTypes.DetailRpt)}
                    />
                    <label className="text-white" >&nbsp;Detail Report</label>
                  </Col>
                  <Col>
                    <input
                      type="radio"
                      id="summary"
                      name="reportType"
                      onChange={() => onChangeReport(reportTypes.SummaryRpt)}
                    />
                    <label className="text-white" >&nbsp;Summary Report</label>
                  </Col>
                  <Col>
                    <input
                      type="radio"
                      id="rating"
                      name="reportType"
                      onChange={() => onChangeReport(reportTypes.DeliveryRatingRpt)}
                    />
                    <label className="text-white" >&nbsp;Delivery Rating Report</label>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="3" style={{ textAlign: "left", marginRight: "5px" }}>
                <Label className="text-white" >Ouput Type:</Label>
              </Col>
              <Col xs="6">
                <Row>
                  <Col>
                    <input
                      type="radio"
                      id="pdf"
                      name="outputType"
                      defaultChecked={true}
                      onChange={() => onChangeOutput(outputType.PDF)}
                    />
                    <label className="text-white" >&nbsp;PDF</label>
                  </Col>
                  <Col>
                    <input
                      type="radio"
                      id="xls"
                      name="outputType"
                      onChange={() => onChangeOutput(outputType.XLS)}
                    />
                    <label className="text-white" >&nbsp;XLS</label>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="3" >
              </Col>
              <Col>
                <Button className="shredButton float-start" disabled={downloading} onClick={runReport}>RUN REPORT</Button>
              </Col>
            </Row>
          {/* </ServiceThreadSpinner> */}          
      </div>      
    </DesktopContainer>
  );
}

function RenderInput({ obj, callback, idx }) {
  if (obj.type === INPUT_TYPES.date) {
    return (
      <Row className="mt-3">
        <Col xs="3" style={{ textAlign: "left", marginRight: "5px" }}>
          <Label className="text-white" for={obj.label}>{obj.name}</Label>
        </Col>
        <Col xs="6">
          <DebounceInput
            type="date"
            onChange={(e) => callback(e, idx, obj.type, obj.label)}
            value={obj.value}
            debounceTimeout={500}
          />
        </Col>
      </Row>
    );
  } else if (obj.type === INPUT_TYPES.select) {
    return (
      <Row className="mt-3">
        <Col xs="3" style={{ textAlign: "left", marginRight: "5px" }}>
          <Label className="text-white" for={obj.label}>{obj.name}</Label>
        </Col>
        <Col xs="6">
          <Select
            classNamePrefix='serviceThreadSelect'
            options={obj.options}
            value={obj.value}
            onChange={(e) => callback(e, idx, obj.type, obj.label)}
          />
        </Col>
      </Row>
    );
  }
  return (
    <Row>
      <Col>
        Nothing here yet
      </Col>
    </Row>
  )
}
